import {
    type CSSProperties,
    useCallback,
    useEffect,
    useState,
    useRef
} from "react";
import {
    useWindowDimensions
} from "react-native";
import {
    NCoreUIKitTheme,
    getCornerStyle,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    useDocsLayout
} from "../theme";
import {
    DocsLink
} from "../router";

export type DocsTocItem = {
    title: string;
    id: string;
};

type TocProps = {
    variant: "rail" | "inline";
    items: Array<DocsTocItem>;
};

type TocLinkProps = {
    onSelect?: (id: string) => void;
    isActive: boolean;
    item: DocsTocItem;
};

const useActiveSection = (items: Array<DocsTocItem>, topOffset: number) => {
    const [
        activeId,
        setActiveId
    ] = useState(items[0]?.id ?? "");

    const lockedStateRef = useRef<{
        id: string | null;
        scrollY: number;
    }>({
        scrollY: 0,
        id: null
    });

    const selectSection = useCallback((id: string) => {
        lockedStateRef.current = {
            scrollY: window.scrollY,
            id
        };

        setActiveId(id);

        requestAnimationFrame(() => {
            lockedStateRef.current.scrollY = window.scrollY;
        });
    }, []);

    useEffect(() => {
        lockedStateRef.current = {
            scrollY: window.scrollY,
            id: null
        };

        const updateActiveSection = () => {
            const elements = items.map((item) => document.getElementById(item.id)).filter((element): element is HTMLElement => element !== null);

            if(elements.length === 0) {
                return;
            }

            if(lockedStateRef.current.id && Math.abs(window.scrollY - lockedStateRef.current.scrollY) <= 4) {
                setActiveId(lockedStateRef.current.id);

                return;
            }

            lockedStateRef.current.id = null;

            const viewportTop = topOffset;
            const viewportBottom = window.innerHeight;
            const viewportCenter = (viewportTop + viewportBottom) / 2;

            let closestId = elements[0]?.id ?? "";
            let smallestDistance = Number.POSITIVE_INFINITY;
            let hasVisibleElement = false;

            elements.forEach((element) => {
                const rect = element.getBoundingClientRect();
                const visibleTop = Math.max(rect.top, viewportTop);
                const visibleBottom = Math.min(rect.bottom, viewportBottom);

                if(visibleBottom > visibleTop) {
                    const visibleCenter = (visibleTop + visibleBottom) / 2;
                    const distance = Math.abs(visibleCenter - viewportCenter);

                    if(!hasVisibleElement || distance < smallestDistance) {
                        smallestDistance = distance;
                        hasVisibleElement = true;
                        closestId = element.id;
                    }

                    return;
                }

                if(hasVisibleElement) {
                    return;
                }

                const distance = Math.min(
                    Math.abs(rect.top - viewportCenter),
                    Math.abs(rect.bottom - viewportCenter)
                );

                if(distance < smallestDistance) {
                    smallestDistance = distance;
                    closestId = element.id;
                }
            });

            setActiveId(closestId);
        };

        updateActiveSection();

        window.addEventListener("scroll", updateActiveSection, {
            passive: true
        });

        window.addEventListener("resize", updateActiveSection);

        return () => {
            window.removeEventListener("scroll", updateActiveSection);
            window.removeEventListener("resize", updateActiveSection);
        };
    }, [
        topOffset,
        items
    ]);

    return {
        selectSection,
        activeId
    };
};

const TocLink = ({
    isActive,
    onSelect,
    item
}: TocLinkProps) => {
    const {
        controlPaddings,
        shapeConfigs,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        isHovered,
        setIsHovered
    ] = useState(false);

    const [
        isFocusVisible,
        setIsFocusVisible
    ] = useState(false);

    const getBackground = () => {
        if(isActive) {
            return colors.content.container.selected;
        }

        if(isHovered) {
            return colors.system.state.overlay.hover.default;
        }

        return "transparent";
    };

    const linkStyle: CSSProperties = {
        padding: `${spaces.spacingSm}px ${controlPaddings.small}px`,
        outlineOffset: isFocusVisible ? borders.focus : undefined,
        outlineColor: colors.system.state.overlay.focus.default,
        outlineWidth: isFocusVisible ? borders.focus : 0,
        ...getCornerStyle({
            radius: radiuses.actions,
            shapeConfigs
        }) as CSSProperties,
        background: getBackground(),
        textDecoration: "none",
        outlineStyle: "solid",
        display: "block"
    };

    return <DocsLink
        onFocus={(event) => setIsFocusVisible(event.currentTarget.matches(":focus-visible"))}
        aria-current={isActive ? "location" : undefined}
        onMouseLeave={() => setIsHovered(false)}
        onMouseEnter={() => setIsHovered(true)}
        onBlur={() => setIsFocusVisible(false)}
        onClick={() => onSelect?.(item.id)}
        href={`#${item.id}`}
        style={linkStyle}
    >
        <Text
            variant={isActive ? "titleSmallSize" : "bodyMediumSize"}
            color={isActive || isHovered ? "high" : "mid"}
        >
            {item.title}
        </Text>
    </DocsLink>;
};

const Toc = ({
    variant,
    items
}: TocProps) => {
    const {
        t
    } = useDocsI18n();

    const {
        controlPaddings,
        shapeConfigs,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const {
        headerHeight
    } = useDocsLayout();

    const {
        selectSection,
        activeId
    } = useActiveSection(items, headerHeight + spaces.spacingLg);

    const isWide = width >= shapeConfigs.wideBreakpoint;

    if(items.length === 0 || (variant === "rail") !== isWide) {
        return null;
    }

    const isRail = variant === "rail";

    const navStyle: CSSProperties = isRail ? {
        padding: `${spaces.spacingXxl}px ${spaces.spacingLg}px ${spaces.spacingXxl}px ${spaces.spacingSm}px`,
        height: `calc(100vh - ${headerHeight}px)`,
        zIndex: shapeConfigs.layers.sticky,
        scrollbarGutter: "stable",
        boxSizing: "border-box",
        position: "sticky",
        overflowY: "auto",
        top: headerHeight
    } : {
        border: `${borders.line}px solid ${colors.content.border.subtle}`,
        padding: `${spaces.spacingMd}px ${spaces.spacingSm}px`,
        marginBottom: spaces.spacingXxl,
        ...getCornerStyle({
            radius: radiuses.surface,
            shapeConfigs
        }) as CSSProperties
    };

    const titleStyle: CSSProperties = {
        padding: `0 ${controlPaddings.small}px`,
        marginBottom: spaces.spacingSm
    };

    const listStyle: CSSProperties = {
        display: isRail ? "block" : "flex",
        gap: spaces.spacingXs,
        flexDirection: "row",
        listStyle: "none",
        flexWrap: "wrap",
        margin: 0,
        padding: 0
    };

    return <nav
        aria-label={t("toc.title")}
        style={navStyle}
    >
        <div
            style={titleStyle}
        >
            <Text
                variant="titleSmallSize"
                color="high"
            >
                {t("toc.title")}
            </Text>
        </div>
        <ul
            style={listStyle}
        >
            {
                items.map((item) => <li
                    key={item.id}
                >
                    <TocLink
                        isActive={isRail && item.id === activeId}
                        onSelect={selectSection}
                        item={item}
                    />
                </li>)
            }
        </ul>
    </nav>;
};
export default Toc;
